import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OttopilotCustomer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotCustomersWorkspaceCapability/domain/OttopilotCustomer";
import type {OttopilotCustomersWorkspaceCapabilityConfiguration as CustomersModuleConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotCustomersWorkspaceCapability/domain/OttopilotCustomersWorkspaceCapabilityConfiguration";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    getDefaultCustomerProfileTabs,
    getDefaultJourneyProfileTabs,
    isProfileTabSupportedForKind,
} from "~/components/entityProfile/profileTabRegistry";
import {resolveCustomerProfileMetaChips} from "~/components/entityProfile/resolveCustomerProfileMetaChips";
import {resolveJourneyProfileMetaChips} from "~/components/entityProfile/resolveEntityProfileMetaChips";
import type {EntityProfileDefinition, EntityProfileKind, EntityProfileSummaryConfig} from "~/components/entityProfile/types";
import type {CustomerProfileSearch} from "~/utilities/customerProfileNavigation";
import {resolveCustomerProfileBreadcrumbs} from "~/utilities/customerProfileNavigation";
import type {EventContainerProfileSearch} from "~/utilities/eventContainerProfileNavigation";
import {resolveEventContainerProfileBreadcrumbs} from "~/utilities/eventContainerProfileNavigation";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export function resolveJourneyEntityProfileDefinition(args: {
    journey: EventContainerWithEvents;
    eventContainerTemplate: EventContainerTemplate;
    eventTemplates: ReadonlyArray<OttopilotEventTemplate>;
    workspaceId: Uuid;
    search: EventContainerProfileSearch;
    isAmaronWorkspace: boolean;
    summary: EntityProfileSummaryConfig;
}): EntityProfileDefinition {
    const configuration = args.eventContainerTemplate.configuration;
    const tabCandidates = getDefaultJourneyProfileTabs().filter((tab) =>
        isProfileTabSupportedForKind(tab.id, "journey"),
    );

    const breadcrumbItems = resolveEventContainerProfileBreadcrumbs({
        from: args.search.from,
        workspaceId: args.workspaceId,
        eventContainerTemplate: args.eventContainerTemplate,
        eventTemplates: args.eventTemplates,
        isAmaronWorkspace: args.isAmaronWorkspace,
    });
    const profileBreadcrumbLabel = breadcrumbItems[breadcrumbItems.length - 1]?.displayName;
    const pageTitle =
        profileBreadcrumbLabel ??
        `${configuration.moduleDisplayNameVariants.singularSentenceCase} profile`;

    return {
        kind: "journey",
        pageTitle,
        breadcrumbItems,
        tabs: tabCandidates,
        defaultTabId: "events",
        summary: args.summary,
        metaChips: resolveJourneyProfileMetaChips({
            journey: args.journey,
            summaryFields: args.summary.fields,
        }),
        headerActionIds: ["contact", "addEvent", "more"],
        footerActionIds: ["logCall", "edit", "create", "more"],
    };
}

/** Customer profile definition for the entity profile route and aside. */
export function resolveCustomerEntityProfileDefinition(args: {
    customer: OttopilotCustomer;
    customersModuleConfiguration: CustomersModuleConfiguration;
    workspaceId: Uuid;
    search: CustomerProfileSearch;
    summary: EntityProfileSummaryConfig;
}): EntityProfileDefinition {
    const configuration = args.customersModuleConfiguration;
    const tabCandidates = getDefaultCustomerProfileTabs().filter((tab) =>
        isProfileTabSupportedForKind(tab.id, "customer"),
    );

    const breadcrumbItems = resolveCustomerProfileBreadcrumbs({
        from: args.search.from,
        workspaceId: args.workspaceId,
        customersPluralLabel: configuration.moduleDisplayName.pluralSentenceCase,
        customersSingularLabel: configuration.moduleDisplayName.singularSentenceCase,
    });
    const profileBreadcrumbLabel = breadcrumbItems[breadcrumbItems.length - 1]?.displayName;
    const pageTitle = profileBreadcrumbLabel ?? args.summary.title;

    return {
        kind: "customer",
        pageTitle,
        breadcrumbItems,
        tabs: tabCandidates,
        defaultTabId: "overview",
        summary: args.summary,
        metaChips: resolveCustomerProfileMetaChips({
            customer: args.customer,
            summaryFields: args.summary.fields,
        }),
        headerActionIds: ["contact", "more"],
        footerActionIds: ["edit", "create", "more"],
    };
}

/** Journey profile definition stub for Storybook. */
export function resolveJourneyEntityProfileDefinitionMock(args: {
    pageTitle: string;
    breadcrumbItems: EntityProfileDefinition["breadcrumbItems"];
    summary: EntityProfileDefinition["summary"];
    metaChips?: EntityProfileDefinition["metaChips"];
    /** When true, includes the Details tab (aside / mobile shell). Default false matches desktop page tabs. */
    includeDetailsTab?: boolean;
}): EntityProfileDefinition {
    const tabCandidates = getDefaultJourneyProfileTabs().filter((tab) =>
        isProfileTabSupportedForKind(tab.id, "journey"),
    );
    const tabs =
        args.includeDetailsTab === true
            ? tabCandidates
            : tabCandidates.filter((tab) => tab.id !== "details");

    return {
        kind: "journey",
        pageTitle: args.pageTitle,
        breadcrumbItems: args.breadcrumbItems,
        tabs,
        defaultTabId: "events",
        summary: args.summary,
        metaChips: args.metaChips ?? [],
        headerActionIds: ["contact", "addEvent", "more"],
        footerActionIds: ["logCall", "edit", "create", "more"],
    };
}

/** Customer profile definition stub for Storybook. */
export function resolveCustomerEntityProfileDefinitionMock(args: {
    pageTitle: string;
    breadcrumbItems: EntityProfileDefinition["breadcrumbItems"];
    summary: EntityProfileDefinition["summary"];
}): EntityProfileDefinition {
    const tabCandidates = getDefaultCustomerProfileTabs().filter((tab) =>
        isProfileTabSupportedForKind(tab.id, "customer"),
    );

    return {
        kind: "customer",
        pageTitle: args.pageTitle,
        breadcrumbItems: args.breadcrumbItems,
        tabs: tabCandidates,
        defaultTabId: "overview",
        summary: args.summary,
        metaChips: [],
        headerActionIds: ["contact", "more"],
        footerActionIds: ["edit", "create", "more"],
    };
}

export function filterProfileTabsForKind(
    definition: EntityProfileDefinition,
): EntityProfileDefinition["tabs"] {
    return definition.tabs.filter((tab) => isProfileTabSupportedForKind(tab.id, definition.kind));
}

/** Desktop full-page journey profile uses the left summary card; Details is aside + mobile only. */
export function filterJourneyProfileTabsForShell(args: {
    tabs: EntityProfileDefinition["tabs"];
    presentation: "page" | "aside";
    isMobile: boolean;
}): EntityProfileDefinition["tabs"] {
    if (args.presentation === "aside" || args.isMobile === true) {
        return args.tabs;
    }

    return args.tabs.filter((tab) => tab.id !== "details");
}

/** Desktop full-page customer profile uses the left summary card; Details is aside + mobile only. */
export function filterCustomerProfileTabsForShell(args: {
    tabs: EntityProfileDefinition["tabs"];
    presentation: "page" | "aside";
    isMobile: boolean;
}): EntityProfileDefinition["tabs"] {
    if (args.presentation === "aside" || args.isMobile === true) {
        return args.tabs;
    }

    return args.tabs.filter((tab) => tab.id !== "details");
}

export type {EntityProfileKind};
